import { LineChart } from "@mantine/charts";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Code,
  CopyButton,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  useClearLoginLock,
  useIssueUserKey,
  useRevokeKey,
  useUpdateUser,
  useUserDetail,
  useUserUsage,
} from "@/lib/admin/hooks";
import { fillDailyBuckets, getLast30DayRange } from "@/lib/admin/usage";
import type {
  AdminApiKeyCreatedResponse,
  AdminApiKeyDetail,
  AdminAuditEntrySummary,
  Role,
  Tier,
} from "@/lib/api/client";

const TIER_OPTIONS: { value: Tier; label: string }[] = [
  { value: "free", label: "Free" },
  { value: "premium", label: "Premium" },
];

function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  return dayjs(iso).format("YYYY-MM-DD HH:mm");
}

function roleLabel(role: Role): string {
  return role === "admin" ? "Admin" : "User";
}

function formatAuditDiff(diff: Record<string, unknown> | null): string {
  if (diff === null || Object.keys(diff).length === 0) {
    return "—";
  }
  return JSON.stringify(diff);
}

type IssueKeyFormValues = {
  name: string;
};

/** Admin user detail: profile, usage chart, keys, and recent audit. */
export function UserDetailPage() {
  const { id: userId } = useParams<{ id: string }>();
  const usageRange = useMemo(() => getLast30DayRange(), []);

  const { data: user, isLoading, isError, error } = useUserDetail(userId ?? "");
  const {
    data: usageData,
    isLoading: isUsageLoading,
    isError: isUsageError,
    error: usageError,
  } = useUserUsage(userId ?? "", usageRange.fromISO, usageRange.toISO);

  const updateUser = useUpdateUser();
  const clearLoginLock = useClearLoginLock(userId ?? "");
  const issueKey = useIssueUserKey(userId ?? "");
  const revokeKey = useRevokeKey(userId ?? "");

  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issuedKey, setIssuedKey] = useState<AdminApiKeyCreatedResponse | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<AdminApiKeyDetail | null>(null);

  const issueForm = useForm<IssueKeyFormValues>({
    initialValues: { name: "" },
    validate: {
      name: (value) => (value.trim().length > 0 ? null : "Name is required"),
    },
  });

  const chartData = useMemo(() => {
    if (!usageData) {
      return [];
    }
    return fillDailyBuckets(usageData.items, usageRange.fromISO, usageRange.toISO);
  }, [usageData, usageRange.fromISO, usageRange.toISO]);

  const handleTierChange = (tier: string | null) => {
    if (!userId || (tier !== "free" && tier !== "premium")) {
      return;
    }
    updateUser.mutate({ userId, patch: { tier } });
  };

  const handleActiveChange = (checked: boolean) => {
    if (!userId) {
      return;
    }
    updateUser.mutate({ userId, patch: { is_active: checked } });
  };

  const handleIssueKey = issueForm.onSubmit((values) => {
    issueKey.mutate(
      { name: values.name.trim() },
      {
        onSuccess: (created) => {
          setIssuedKey(created);
          issueForm.reset();
        },
      },
    );
  });

  const handleCloseIssueModal = () => {
    setIssueModalOpen(false);
    setIssuedKey(null);
    issueForm.reset();
  };

  const handleConfirmRevoke = () => {
    if (!revokeTarget) {
      return;
    }
    revokeKey.mutate({ keyId: revokeTarget.id }, { onSuccess: () => setRevokeTarget(null) });
  };

  if (!userId) {
    return (
      <Alert color="red" title="Invalid URL">
        Missing user id in the route.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (isError || !user) {
    return (
      <Stack gap="md">
        <Anchor component={Link} to="/users" size="sm">
          ← Back to users
        </Anchor>
        <Alert color="red" title="Failed to load user">
          {error instanceof Error ? error.message : "An unexpected error occurred."}
        </Alert>
      </Stack>
    );
  }

  const isProfileUpdating = updateUser.isPending && updateUser.variables?.userId === userId;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Anchor component={Link} to="/users" size="sm">
            ← Back to users
          </Anchor>
          <Title order={2}>{user.email}</Title>
          <Text c="dimmed" size="sm">
            User profile, API keys, usage, and audit history.
          </Text>
        </Stack>
      </Group>

      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Title order={4}>Profile</Title>
            {user.login_locked ? (
              <Badge color="red" variant="light" data-testid="login-lock-badge">
                Login locked until {formatDateTime(user.login_locked_until ?? null)}
              </Badge>
            ) : null}
          </Group>
          <Group grow align="flex-start" wrap="wrap">
            <Stack gap={4}>
              <Text size="sm" c="dimmed">
                Display name
              </Text>
              <Text size="sm">{user.display_name ?? "—"}</Text>
            </Stack>
            <Stack gap={4}>
              <Text size="sm" c="dimmed">
                Role
              </Text>
              <Text size="sm">{roleLabel(user.role)}</Text>
            </Stack>
            <Stack gap={4}>
              <Text size="sm" c="dimmed">
                Email verified
              </Text>
              <Text size="sm">{formatDateTime(user.email_verified_at)}</Text>
            </Stack>
            <Stack gap={4}>
              <Text size="sm" c="dimmed">
                Created
              </Text>
              <Text size="sm">{formatDateTime(user.created_at)}</Text>
            </Stack>
            <Stack gap={4}>
              <Text size="sm" c="dimmed">
                Updated
              </Text>
              <Text size="sm">{formatDateTime(user.updated_at)}</Text>
            </Stack>
          </Group>
          <Group align="flex-end" wrap="wrap">
            <Select
              label="Tier"
              data={TIER_OPTIONS}
              value={user.tier}
              onChange={handleTierChange}
              disabled={isProfileUpdating}
              w={160}
            />
            <Switch
              label="Active account"
              checked={user.is_active}
              onChange={(event) => handleActiveChange(event.currentTarget.checked)}
              disabled={isProfileUpdating}
            />
            <Button
              variant="light"
              color="orange"
              data-testid="unlock-login-button"
              disabled={!user.login_locked || clearLoginLock.isPending}
              loading={clearLoginLock.isPending}
              onClick={() => clearLoginLock.mutate()}
            >
              Unlock login
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Title order={4}>Usage (last 30 days)</Title>
          {isUsageLoading ? (
            <Center py="md">
              <Loader />
            </Center>
          ) : null}
          {isUsageError ? (
            <Alert color="red" title="Failed to load usage">
              {usageError instanceof Error ? usageError.message : "An unexpected error occurred."}
            </Alert>
          ) : null}
          {!isUsageLoading && !isUsageError ? (
            chartData.every((row) => row.requests === 0 && row.errors === 0) ? (
              <Text c="dimmed" ta="center" py="md">
                No usage recorded in this period.
              </Text>
            ) : (
              <LineChart
                h={280}
                data={chartData}
                dataKey="date"
                series={[
                  { name: "requests", color: "blue.6", label: "Requests" },
                  { name: "errors", color: "red.6", label: "Errors" },
                ]}
                curveType="linear"
                withLegend
                withTooltip
              />
            )
          ) : null}
        </Stack>
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={4}>API keys</Title>
            <Button size="compact-sm" onClick={() => setIssueModalOpen(true)}>
              Issue key
            </Button>
          </Group>
          {user.api_keys.length === 0 ? (
            <Text c="dimmed" ta="center" py="md">
              No API keys for this user.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={640}>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Prefix</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Last used</Table.Th>
                    <Table.Th>Created</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {user.api_keys.map((key) => (
                    <ApiKeyRow
                      key={key.id}
                      apiKey={key}
                      onRevoke={() => setRevokeTarget(key)}
                      isRevoking={revokeKey.isPending && revokeKey.variables?.keyId === key.id}
                    />
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Title order={4}>Recent audit</Title>
          {user.recent_audit.length === 0 ? (
            <Text c="dimmed" ta="center" py="md">
              No audit entries for this user.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={720}>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>When</Table.Th>
                    <Table.Th>Action</Table.Th>
                    <Table.Th>Actor</Table.Th>
                    <Table.Th>Before</Table.Th>
                    <Table.Th>After</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {user.recent_audit.map((entry) => (
                    <AuditRow key={entry.id} entry={entry} />
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Card>

      <Modal
        opened={issueModalOpen}
        onClose={handleCloseIssueModal}
        title={issuedKey ? "API key created" : "Issue API key"}
        centered
      >
        {issuedKey ? (
          <Stack gap="md">
            <Alert color="yellow" title="Save this key now">
              This is the only time the full API key will be shown. Copy it before closing this
              dialog.
            </Alert>
            <Code block>{issuedKey.plaintext}</Code>
            <Group>
              <CopyButton value={issuedKey.plaintext}>
                {({ copied, copy }) => (
                  <Button color={copied ? "teal" : "blue"} onClick={copy}>
                    {copied ? "Copied" : "Copy key"}
                  </Button>
                )}
              </CopyButton>
              <Button variant="light" onClick={handleCloseIssueModal}>
                Done
              </Button>
            </Group>
          </Stack>
        ) : (
          <form onSubmit={handleIssueKey}>
            <Stack gap="md">
              <TextInput
                label="Key name"
                placeholder="e.g. Support issued"
                required
                {...issueForm.getInputProps("name")}
              />
              <Group justify="flex-end">
                <Button variant="default" onClick={handleCloseIssueModal}>
                  Cancel
                </Button>
                <Button type="submit" loading={issueKey.isPending}>
                  Issue key
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Modal>

      <Modal
        opened={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        title="Revoke API key"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Revoke key <strong>{revokeTarget?.name}</strong> ({revokeTarget?.prefix})? This cannot
            be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button color="red" loading={revokeKey.isPending} onClick={handleConfirmRevoke}>
              Revoke
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

type ApiKeyRowProps = {
  apiKey: AdminApiKeyDetail;
  onRevoke: () => void;
  isRevoking: boolean;
};

function ApiKeyRow({ apiKey, onRevoke, isRevoking }: ApiKeyRowProps) {
  const isRevoked = apiKey.revoked_at !== null;

  return (
    <Table.Tr style={isRevoked ? { opacity: 0.65 } : undefined}>
      <Table.Td>
        <Text size="sm">{apiKey.name}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" ff="monospace">
          {apiKey.prefix}…
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge color={isRevoked ? "gray" : "green"} variant="light">
          {isRevoked ? "Revoked" : "Active"}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{formatDateTime(apiKey.last_used_at)}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{formatDateTime(apiKey.created_at)}</Text>
      </Table.Td>
      <Table.Td>
        {!isRevoked ? (
          <Button
            size="compact-xs"
            color="red"
            variant="light"
            loading={isRevoking}
            onClick={onRevoke}
          >
            Revoke
          </Button>
        ) : (
          <Tooltip label={formatDateTime(apiKey.revoked_at)}>
            <Text size="xs" c="dimmed">
              Revoked
            </Text>
          </Tooltip>
        )}
      </Table.Td>
    </Table.Tr>
  );
}

type AuditRowProps = {
  entry: AdminAuditEntrySummary;
};

function AuditRow({ entry }: AuditRowProps) {
  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm">{formatDateTime(entry.created_at)}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" ff="monospace">
          {entry.action}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c={entry.actor_user_id ? undefined : "dimmed"}>
          {entry.actor_user_id ?? "—"}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="xs" ff="monospace" lineClamp={2}>
          {formatAuditDiff(entry.before_diff)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="xs" ff="monospace" lineClamp={2}>
          {formatAuditDiff(entry.after_diff)}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
}
